/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useState, useCallback, useEffect, useMemo} from 'react';
// redux
import {useSelector, useDispatch} from 'react-redux';
import {
  PRODUCT_ARTICLE_SENTIMENT,
  PRODUCT_REQUEST_PENDING,
  sendArticleSentimentAccessRequest,
  updateSiteConfiguration
} from "../../../../../state/thunks/engagement";
// material
import {Button, InlineMessage} from '@insticator/insticator-ui';
import ExpandableSection from '../../../../../material/expandablesection/expandablesection';
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller';
import GeneralConfigs, {FEEDBACK_MAX_LENGTH} from './general/general';
import ColorScheme from './colorscheme/colorscheme';
import ExternalPageID from './externalpageid/externalpageid';
import MarketingPage from './marketing/marketing';
// styles
import injectSheet from 'react-jss';
import styles from './sentiment_styles';
import {getTestPage} from "../../../../../state/thunks/api/asu";
import {DEFAULT_COLORS} from "../../../../../state/slices/engagement";


/* ========== ~~~~~~~~~~ SENTIMENT CONFIG ~~~~~~~~~~ ========== */
const SentimentConfig = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const {
    engagement,
    session: {
      accountUUID,
      mocked,
    },
    engagement: {
      isLoading,
      accountProducts,
      articleSentimentUnitUUID,
      articleSentimentAccessRequest: {
        requestStatus,
      } = {},
    }
  } = useSelector((state) => state);

  const pageId = null     // TODO get pageId

  const [isOpened, setIsOpened] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [config, setConfig] = useState(engagement);
  const [defaultConfig, setDefaultConfig] = useState(engagement);

  const hasArticleSentimentAccess = useMemo(() =>
      accountProducts.includes(PRODUCT_ARTICLE_SENTIMENT)
    , [accountProducts]
  )

  useEffect(async () => {
    setConfig(engagement)
    setDefaultConfig(engagement)
  }, [engagement])

  const handleToggle = useCallback(() => {
    setIsOpened(!isOpened)
  }, [isOpened]);

  const handleCancel = useCallback(() => {
    setIsOpened(false);
    setIsChanged(false);
    setConfig(defaultConfig)
  }, [defaultConfig]);

  const handleSave = useCallback(async () => {
    await dispatch(updateSiteConfiguration(config))
    setIsOpened(false)
    setIsChanged(false)
  }, [config, dispatch]);

  const handleChangeFeedback = useCallback((e) => {
    setConfig({
      ...config,
      title: e.target.value.substring(0, FEEDBACK_MAX_LENGTH),
    })
    setIsChanged(true)
  }, [config])

  const handleChangeOverview = useCallback((enableOverview) => {
    setConfig({
      ...config,
      enableOverview,
    })
    setIsChanged(true)
  }, [config])

  const handleChangeTimeRange = useCallback((overviewTimeRange) => {
    setConfig({
      ...config,
      overviewTimeRange,
    })
    setIsChanged(true)
  }, [config])

  const handleSetColour = useCallback((color, value) => {
    setConfig({
      ...config,
      colorConfiguration: {
        ...config.colorConfiguration,
        [color]: value.replaceAll('#', '').toUpperCase(),
      },
    })
    setIsChanged(true)
  }, [config]);

  const handleResetColour = useCallback((color) => {
    const value = defaultConfig.colorConfiguration[color]
    handleSetColour(color, value)
  }, [defaultConfig, handleSetColour]);

  const handleChangeEnablePageId = useCallback((enablePageId) => {
    setConfig({
      ...config,
      enablePageId,
    })
    setIsChanged(true)
  }, [config])

  const handleGetAccess = useCallback(() => {
    if (hasArticleSentimentAccess) return;
    dispatch(sendArticleSentimentAccessRequest(accountUUID))
  }, [dispatch, hasArticleSentimentAccess, accountUUID])

  const testPageUrl = useMemo(() =>
      articleSentimentUnitUUID
        ? getTestPage({articleSentimentUnitUUID, pageId})
        : null
    , [articleSentimentUnitUUID, pageId])

  const handleOpenTestPage = useCallback(() =>
      window.open(testPageUrl, '_blank').focus()
    , [testPageUrl])

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={hasArticleSentimentAccess ? handleSave : null}
      isOpened={isOpened}
      isChanged={isChanged}
      isLoading={isLoading}
      title='Article Sentiment'
      description={mocked && hasArticleSentimentAccess && testPageUrl
        ? <Button
            variant={'system'}
            text={'Test page'}
            type={'action'}
            handleClick={handleOpenTestPage}
          />
        : ''
      }
    />
  );
  return (
    <ExpandableSection
      className={`sentimentconfig ${props.classes.sentimentconfig}`}
      controller={controller}
      handleCancel={handleCancel}
      handleSave={hasArticleSentimentAccess ? handleSave : null}
      isOpened={isOpened}
      isLoading={isLoading}
      isChanged={isChanged}
    >
      {isOpened && <>
        {requestStatus === PRODUCT_REQUEST_PENDING && <>
          <InlineMessage
            name={'emojimessage'}
            variant={'none'}
            icon={'exclamation-triangle'}
            message={'Please wait while we are processing your request.'}
            display={true}
            title={''}
            close={false}
          />
        </>}
        {!hasArticleSentimentAccess && requestStatus !== PRODUCT_REQUEST_PENDING && <MarketingPage onRequest={handleGetAccess} />}

        {hasArticleSentimentAccess && <>
          <GeneralConfigs
            handleChangeOverview={handleChangeOverview}
            handleChangeFeedback={handleChangeFeedback}
            handleChangeTimeRange={handleChangeTimeRange}
            config={config}
          />
          <ColorScheme
            handleResetColour={handleResetColour}
            handleSetColour={handleSetColour}
            config={config}
            defaultConfig={defaultConfig}
          />
          <ExternalPageID
            handleChangeEnablePageId={handleChangeEnablePageId}
            enablePageId={config.enablePageId}
          />
        </>}
      </>}
    </ExpandableSection>
  );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default SentimentConfig;
